'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/lib/i18n/language-provider';
import { useRouter } from 'next/navigation';
import {
  Play,
  Crown,
  Timer,
  Cpu,
  Cable,
  Sparkles,
  ChevronRight,
  Zap,
  Check,
  Download,
  ArrowLeft,
} from 'lucide-react';
import { DEMO_CONFIG } from '@/hooks/use-demo-mode';
import { navigateOrOpenBrowser } from '@/lib/tauri-navigation';

interface DemoWelcomeProps {
  onStartDemo: () => void;
  canRestartDemo: boolean;
  cooldownRemaining: number;
  formatTime: (seconds: number) => string;
}

export function DemoWelcome({
  onStartDemo,
  canRestartDemo,
  cooldownRemaining,
  formatTime,
}: DemoWelcomeProps) {
  const { t } = useLanguage();
  const router = useRouter();
  const [currentStep, setCurrentStep] = useState(0);

  const onboardingSteps = [
    {
      icon: <Cpu className="h-8 w-8" />,
      title: t('demo.onboarding.step1.title') || 'Ajoutez des processeurs',
      description: t('demo.onboarding.step1.desc') || 'Glissez-déposez vos processeurs vidéo NovaStar, Brompton ou autres sur le workspace.',
    },
    {
      icon: <Cable className="h-8 w-8" />,
      title: t('demo.onboarding.step2.title') || 'Connectez vos panneaux',
      description: t('demo.onboarding.step2.desc') || 'Créez des liaisons visuelles entre processeurs et panneaux LED avec un système de câblage intuitif.',
    },
    {
      icon: <Zap className="h-8 w-8" />,
      title: t('demo.onboarding.step3.title') || 'Gérez l\'alimentation',
      description: t('demo.onboarding.step3.desc') || 'Configurez les armoires électriques et visualisez la distribution de puissance.',
    },
    {
      icon: <Download className="h-8 w-8" />,
      title: t('demo.onboarding.step4.title') || 'Exportez vos synoptiques',
      description: t('demo.onboarding.step4.desc') || 'Générez des exports PNG, PDF ou vidéo pour partager vos schémas professionnels.',
    },
  ];

  const proFeatures = [
    { text: t('demo.pro.unlimited') || 'Éléments illimités', icon: <Check className="h-4 w-4" /> },
    { text: t('demo.pro.save') || 'Sauvegarde cloud', icon: <Check className="h-4 w-4" /> },
    { text: t('demo.pro.export') || 'Export haute résolution', icon: <Check className="h-4 w-4" /> },
    { text: t('demo.pro.collaboration') || 'Collaboration en temps réel', icon: <Check className="h-4 w-4" /> },
  ];

  const formatCooldown = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${mins}min`;
    }
    return `${mins}min`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden bg-gradient-to-br from-background via-background to-muted/30">
      {/* Animated background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[10%] left-[5%] w-96 h-96 bg-primary/5 rounded-full blur-[100px] animate-pulse" />
        <div className="absolute bottom-[20%] right-[10%] w-80 h-80 bg-blue-500/5 rounded-full blur-[100px] animate-pulse delay-1000" />
        <div className="absolute top-[50%] left-[50%] w-64 h-64 bg-purple-500/5 rounded-full blur-[100px] animate-pulse delay-2000" />
      </div>

      {/* Grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:60px_60px]" />

      {/* Back button */}
      <button
        onClick={() => router.push('/')}
        className="absolute top-6 left-6 z-20 flex items-center gap-2 px-4 py-2 rounded-lg bg-background/80 backdrop-blur-sm border border-border/50 hover:bg-background transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        <span className="text-sm font-medium">{t('common.back') || 'Retour'}</span>
      </button>

      <div className="relative z-10 w-full max-w-5xl">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left side - Demo info */}
          <div className="space-y-6">
            <div className="space-y-2">
              <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20">
                <Sparkles className="h-3 w-3 mr-1" />
                {t('demo.badge') || 'Mode Démonstration'}
              </Badge>
              <h1 className="text-4xl font-bold tracking-tight">
                {t('demo.welcome.title') || 'Découvrez le Synoptique'}
              </h1>
              <p className="text-lg text-muted-foreground">
                {t('demo.welcome.subtitle') || 'Testez gratuitement notre outil professionnel de schématisation LED'}
              </p>
            </div>

            {/* Onboarding steps */}
            <div className="space-y-4">
              {onboardingSteps.map((step, index) => (
                <div
                  key={index}
                  className={`flex items-start gap-4 p-4 rounded-lg border transition-all cursor-pointer ${
                    currentStep === index
                      ? 'border-primary bg-primary/5'
                      : 'border-border/50 hover:border-border'
                  }`}
                  onClick={() => setCurrentStep(index)}
                >
                  <div className={`p-2 rounded-lg ${
                    currentStep === index ? 'bg-primary text-primary-foreground' : 'bg-muted'
                  }`}>
                    {step.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold">{step.title}</h3>
                    <p className="text-sm text-muted-foreground">{step.description}</p>
                  </div>
                  <ChevronRight className={`h-5 w-5 transition-transform ${
                    currentStep === index ? 'text-primary rotate-90' : 'text-muted-foreground'
                  }`} />
                </div>
              ))}
            </div>

          </div>

          {/* Right side - Actions */}
          <div className="flex flex-col justify-center space-y-6">
            {/* Demo Start Card */}
            <div className="p-6 rounded-xl border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded-full bg-primary/10">
                  <Play className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">
                    {t('demo.start.title') || 'Commencer la démo'}
                  </h3>
                </div>
              </div>

              {canRestartDemo ? (
                <Button
                  size="lg"
                  className="w-full h-14 text-lg bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
                  onClick={onStartDemo}
                >
                  <Play className="mr-2 h-5 w-5" />
                  {t('demo.start.button') || 'Démarrer la démonstration'}
                </Button>
              ) : (
                <div className="space-y-3">
                  <Button size="lg" className="w-full h-14 text-lg" disabled>
                    <Timer className="mr-2 h-5 w-5" />
                    {t('demo.cooldown.button') || 'Démo temporairement indisponible'}
                  </Button>
                  <p className="text-sm text-center text-muted-foreground">
                    {t('demo.cooldown.message') || 'Nouvelle démo disponible dans'}{' '}
                    <span className="font-medium text-foreground">{formatCooldown(cooldownRemaining)}</span>
                  </p>
                </div>
              )}
            </div>

            {/* Pro upgrade Card */}
            <div className="p-6 rounded-xl border-2 border-yellow-500/30 bg-gradient-to-br from-yellow-500/5 to-orange-500/5">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500">
                  <Crown className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">
                    {t('demo.pro.title') || 'Passez en Pro'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t('demo.pro.subtitle') || 'Accès complet sans limitations'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-4">
                {proFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm">
                    <div className="text-green-500">{feature.icon}</div>
                    <span>{feature.text}</span>
                  </div>
                ))}
              </div>

              <Button
                size="lg"
                variant="outline"
                className="w-full h-12 border-yellow-500/50 hover:bg-yellow-500/10 hover:border-yellow-500"
                onClick={() => navigateOrOpenBrowser('/pro', router)}
              >
                <Crown className="mr-2 h-5 w-5 text-yellow-500" />
                {t('demo.pro.button') || 'Découvrir Pro - 9,99€/6 mois'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
